// ============================================================================
// ProjectActionCard — every project action's card (item #944, the projects
// part), built like the Buildings card to Ben's approved card demo
// (docs/build-room/demos/item-944.html): the action and the project in the
// title, the screen's own words, the read-back and a button naming the
// consequence on a card that moves money or cannot be undone, Do it at the
// bottom, every control ≥ 44 px, locked once saved. What each action shows is
// projectCard() in ../project-actions.ts; the save is commitProjectAction().
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { CopilotProposalBase } from '../../../shared/types';
import { fetchProject } from '../../project-work/hooks/use-project-data';
import { savedProjectBlockers } from '../../project-work/lib/ready-gate';
import { projectCard, projectOf, type ProjectDraft } from '../project-actions';
import { CardShell } from './card-shell';
import { FieldControl } from './building-action-card';

interface Props {
  proposal: CopilotProposalBase;
  isCommitting: boolean;
  onDoIt: (edited_input: Record<string, unknown>, was_edited: boolean) => void;
  onDiscard: () => void;
}

export function ProjectActionCard({ proposal, isCommitting, onDoIt, onDiscard }: Props) {
  const draft = proposal.input as unknown as ProjectDraft;
  const start = useMemo(() => ({ ...(draft.fields ?? {}) }), [draft]);
  const [fields, setFields] = useState<Record<string, unknown>>(start);
  const card = projectCard(draft, fields);
  const wasEdited = JSON.stringify(fields) !== JSON.stringify(start);
  const open = proposal.status === 'Proposed';

  // Draft → finance: the screen's own blockers, on the project as it is now.
  const toFinance = draft.action === 'move_stage' && fields.to === 'Ready_to_Invoice';
  const [blockers, setBlockers] = useState<string[] | null>(null);
  useEffect(() => {
    if (!toFinance || !open) return;
    let cancelled = false;
    fetchProject(draft.project_id)
      .then((p) => { if (!cancelled) setBlockers(p ? savedProjectBlockers(p) : ['the project could not be read']); })
      .catch(() => { if (!cancelled) setBlockers(['the project could not be read']); });
    return () => { cancelled = true; };
  }, [toFinance, open, draft.project_id]);
  const blocked = toFinance && blockers != null && blockers.length > 0
    ? `Before this can go to finance: ${blockers.join(' · ')}. Fix it on the project.`
    : null;

  return (
    <CardShell
      title={`${card.title} · ${projectOf(draft)}`}
      icon={<ClipboardList className="h-4 w-4" />}
      accentClass={card.readBack ? 'border-st-warn-border-strong' : 'border-tcs-border'}
      status={proposal.status}
      committedLabel="Saved"
      isCommitting={isCommitting}
      doItDisabled={!!card.missing || !!blocked || (toFinance && blockers == null)}
      doItDisabledReason={card.missing ?? blocked ?? (toFinance && blockers == null ? 'Checking the project…' : undefined)}
      readBack={card.readBack ?? undefined}
      doItLabel={card.doItLabel ?? undefined}
      onDoIt={() => onDoIt({ ...draft, fields }, wasEdited)}
      onDiscard={onDiscard}
      after={
        <Link to={`/project-work/${draft.project_id}`} className="text-sm font-medium text-tcs-text underline">
          Open the project
        </Link>
      }
    >
      {card.info.map((i) => (
        <div key={i.label} className="flex min-h-[44px] items-center justify-between gap-3 rounded-md border border-tcs-border px-2 py-1.5 text-sm">
          <span className="text-xs text-tcs-text-muted">{i.label}</span>
          <span className="text-right text-tcs-text">{i.value}</span>
        </div>
      ))}
      {card.fields.map((spec) => (
        <FieldControl key={spec.key} spec={spec} value={fields[spec.key]} onChange={(v) => setFields((p) => ({ ...p, [spec.key]: v }))} />
      ))}
    </CardShell>
  );
}
